import { useState, useEffect, useMemo, useCallback, useRef } from "react"
import { Link, useParams, useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Separator } from "@/components/ui/separator"
import {
  CalendarDays,
  Tag,
  User,
  MessageCircle,
  Heart,
  Share2,
  ArrowLeft,
  Loader2,
  Eye,
  Clock,
  ThumbsUp,
  Reply
} from "lucide-react"
import Seo from "@/seo/seo"
import { getCanonicalUrl, getAbsoluteImageUrl } from "@/utils/seo-config"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import {
  getBlogById,
  createComment,
  selectCurrentBlog,
  selectCurrentBlogLoading,
  selectCurrentBlogError,
  selectCreatingComment,
  clearCurrentBlog,
} from "@/store/slices/blogSlice"
import { normalizeComment } from "@/utils/helper/blog"
import Loader from "@/components/loader/loader"
import { toast } from "sonner"

// Custom hooks for better code organization
const useBlogData = (slug) => {
  const dispatch = useAppDispatch()
  const blogPost = useAppSelector(selectCurrentBlog)
  const loading = useAppSelector(selectCurrentBlogLoading)
  const error = useAppSelector(selectCurrentBlogError)
  const lastFetchedSlug = useRef(null)

  useEffect(() => {
    // Only fetch if slug exists and is different from last fetched
    if (slug && slug !== lastFetchedSlug.current) {
      lastFetchedSlug.current = slug
      dispatch(getBlogById(slug))
    }
    
    return () => {
      // Clear when component unmounts or slug changes
      if (!slug || slug !== lastFetchedSlug.current) {
        lastFetchedSlug.current = null
        dispatch(clearCurrentBlog())
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]) // dispatch is stable from useAppDispatch, but ESLint wants it - we ignore to prevent issues

  return { blogPost, loading, error }
}

const useComments = (blogPost) => {
  return useMemo(() => {
    if (blogPost?.comments && Array.isArray(blogPost.comments)) {
      return blogPost.comments.map(normalizeComment)
    }
    return []
  }, [blogPost])
}

const useCommentForm = () => {
  const [newComment, setNewComment] = useState({
    name: "",
    email: "",
    comment: "",
  })
  const [replyingTo, setReplyingTo] = useState(null)

  const updateComment = useCallback((field, value) => {
    setNewComment(prev => ({
      ...prev,
      [field]: value,
    }))
  }, [])

  const resetComment = useCallback(() => {
    setNewComment({ name: "", email: "", comment: "" })
    setReplyingTo(null)
  }, [])

  return {
    newComment,
    replyingTo,
    setReplyingTo,
    updateComment,
    resetComment,
  }
}

export default function BlogDetailsPage() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const isSubmitting = useAppSelector(selectCreatingComment)

  const { blogPost, loading, error } = useBlogData(slug)
  const comments = useComments(blogPost)
  const commentForm = useCommentForm()
  const commentFormRef = useRef(null)

  // Handle automatic navigation for 404 errors
  useEffect(() => {
    if (error && !loading) {
      const isNotFound = error.includes("not found") || error.includes("404")
      if (isNotFound) {
        const timer = setTimeout(() => navigate("/blogs"), 2000)
        return () => clearTimeout(timer)
      }
    }
  }, [error, loading, navigate])

  // Enhanced comment submission with better error handling
  const handleCommentSubmit = useCallback(async (e) => {
    e.preventDefault()

    const { name, email, comment } = commentForm.newComment

    // Validation with user feedback
    if (!name?.trim()) {
      toast.error("Name is required")
      return
    }

    if (!email?.trim()) {
      toast.error("Email is required")
      return
    }

    if (!comment?.trim()) {
      toast.error("Comment cannot be empty")
      return
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address")
      return
    }

    if (!blogPost?.id) {
      toast.error("Unable to submit comment. Blog post not found.")
      return
    }

    if (!blogPost?.slug) {
      toast.error("Unable to refresh comments. Invalid blog post.")
      return
    }

    try {
      const commentData = {
        name: name.trim(),
        email: email.trim(),
        comment: comment.trim(),
      }

      if (commentForm.replyingTo) {
        commentData.parentId = commentForm.replyingTo.id
      }

      await dispatch(createComment({ blogId: blogPost.id, commentData })).unwrap()

      // Refetch blog to get updated comments
      if (slug) {
        await dispatch(getBlogById(slug)).unwrap()
      }

      // Success feedback and form reset
      toast.success(commentForm.replyingTo ? "Reply posted successfully!" : "Comment posted successfully!")
      commentForm.resetComment()

      // Scroll to top of comments section
      setTimeout(() => {
        document.getElementById("comments-section")?.scrollIntoView({
          behavior: "smooth",
          block: "start"
        })
      }, 100)

    } catch (err) {
      console.error("Error submitting comment:", err)
      toast.error("Failed to post comment. Please try again.")
    }
  }, [commentForm, blogPost?.id, slug, dispatch])

  // Enhanced date formatting with better error handling and accessibility
  const formatDate = useCallback((dateString, options = {}) => {
    if (!dateString) return ""

    try {
      const date = new Date(dateString)
      if (isNaN(date.getTime())) {
        return dateString
      }

      // Default options for better accessibility
      const defaultOptions = {
        year: "numeric",
        month: "long",
        day: "numeric",
        ...options
      }

      return new Intl.DateTimeFormat("en-US", defaultOptions).format(date)
    } catch (e) {
      console.warn("Date formatting error:", e)
      return dateString
    }
  }, [])

  // Calculate total comments count including replies with memoization
  const getTotalCommentsCount = useCallback((commentsList) => {
    return commentsList.reduce((total, comment) => {
      total += 1 // Count the main comment
      if (comment.replies && Array.isArray(comment.replies)) {
        total += comment.replies.length
      }
      return total
    }, 0)
  }, [])

  // Handle reply button click with better UX
  const handleReplyClick = useCallback((comment) => {
    commentForm.setReplyingTo(comment)

    // Focus management for accessibility
    setTimeout(() => {
      const formElement = document.getElementById("comment-form")
      if (formElement) {
        formElement.scrollIntoView({ behavior: "smooth", block: "center" })
        // Focus the comment textarea for immediate typing
        const textarea = formElement.querySelector("textarea")
        if (textarea) {
          textarea.focus()
        }
      }
    }, 100)
  }, [commentForm])

  // Enhanced reading time calculation
  const calculateReadingTime = useCallback((content, wordsPerMinute = 200) => {
    if (!content) return 0

    // Remove HTML tags and count words
    const text = content.replace(/<[^>]*>/g, '')
    const words = text.trim().split(/\s+/).length
    const minutes = Math.ceil(words / wordsPerMinute)

    return Math.max(1, minutes) // Minimum 1 minute
  }, [])

  // Social sharing functionality
  const handleShare = useCallback(async (platform) => {
    if (!blogPost) return

    const url = window.location.href
    const title = blogPost?.title || 'Blog Post'
    const text = blogPost?.excerpt || `Check out this article: ${title}`

    try {
      if (navigator.share && platform === 'native') {
        await navigator.share({
          title,
          text,
          url,
        })
        toast.success("Shared successfully!")
        return
      }

      // Fallback sharing URLs
      const shareUrls = {
        twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
        linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      }

      if (shareUrls[platform]) {
        window.open(shareUrls[platform], '_blank', 'noopener,noreferrer')
      }
    } catch (error) {
      console.error("Sharing failed:", error)
      toast.error("Sharing failed. You can copy the URL manually.")
    }
  }, [blogPost])

  // Enhanced SEO data preparation
  const seoData = useMemo(() => {
    // Handle null blogPost (loading/error states)
    if (!blogPost) {
      return {
        publishDate: new Date().toISOString(),
        seoTags: [],
        articleData: {},
        readingTime: 0
      }
    }

    const publishDate = blogPost.publishDate
      ? (() => {
          try {
            const date = new Date(blogPost.publishDate)
            return isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString()
          } catch {
            return new Date().toISOString()
          }
        })()
      : new Date().toISOString()

    // Ensure tags is an array of strings for SEO
    const seoTags = Array.isArray(blogPost.tags)
      ? blogPost.tags.filter(tag => tag && typeof tag === 'string').map(tag => String(tag))
      : []

    // Build article object with only valid string values
    const articleData = {}

    // Only add properties that are valid strings
    if (blogPost.author && typeof blogPost.author === 'string') {
      articleData.author = String(blogPost.author)
    }

    if (blogPost.category && typeof blogPost.category === 'string') {
      articleData.section = String(blogPost.category)
    }

    // Ensure publishDate is a valid ISO string
    if (publishDate && typeof publishDate === 'string') {
      articleData.datePublished = publishDate
      articleData.dateModified = publishDate
    }

    // Only add tags if they exist and are valid
    if (Array.isArray(seoTags) && seoTags.length > 0) {
      const validTags = seoTags.filter(tag => tag && typeof tag === 'string')
      if (validTags.length > 0) {
        articleData.tags = validTags
      }
    }

    return {
      publishDate,
      seoTags,
      articleData,
      readingTime: calculateReadingTime(blogPost.content)
    }
  }, [blogPost, calculateReadingTime])

  // Get absolute image URL for social media sharing
  // Use primitive string value for stable dependency to prevent infinite re-renders
  // MUST be called before any early returns to follow Rules of Hooks
  const featuredImageString = blogPost?.featuredImage ? String(blogPost.featuredImage) : null;
  
  const absoluteImageUrl = useMemo(() => {
    if (!featuredImageString) return undefined;
    // Skip placeholder images
    if (featuredImageString === '/placeholder.svg' || featuredImageString.includes('placeholder')) {
      return undefined;
    }
    try {
      return getAbsoluteImageUrl(featuredImageString);
    } catch (error) {
      console.warn('Error generating absolute image URL:', error);
      return undefined;
    }
  }, [featuredImageString]); // Use primitive string for stable dependency

  // Enhanced comment rendering with better accessibility and UX
  const renderComment = useCallback((comment, isReply = false, depth = 0) => {
    const maxDepth = 3 // Limit nesting depth for better UX

    return (
      <article
        key={comment.id}
        className={`${isReply ? "ml-6 mt-4 border-l-2 border-muted pl-4" : ""} ${depth > 0 ? "opacity-90" : ""}`}
        aria-label={`Comment by ${comment.author}`}
      >
        <Card className="transition-shadow hover:shadow-md">
          <CardContent className="pt-6">
            <header className="flex items-start gap-4">
              <Avatar className="w-10 h-10 flex-shrink-0 ring-2 ring-background">
                <AvatarImage
                  src={comment.avatar || "/placeholder.svg"}
                  alt={`${comment.author}'s avatar`}
                  loading="lazy"
                />
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {comment.author.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <h4 className="font-semibold text-sm text-foreground">
                    {comment.author}
                  </h4>
                  {comment.isAdmin && (
                    <Badge
                      variant="secondary"
                      className="text-xs"
                      aria-label="Administrator"
                    >
                      Admin
                    </Badge>
                  )}
                  <time
                    className="text-xs text-muted-foreground"
                    dateTime={comment.date}
                    title={formatDate(comment.date, {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit"
                    })}
                  >
                    {formatDate(comment.date)}
                  </time>
                </div>
                <div
                  className="text-sm text-gray-700 leading-relaxed mb-3 prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: comment.content }}
                />
                {/* Action buttons */}
                <div className="flex items-center gap-2">
                  {!isReply && depth < maxDepth && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs h-7 px-2 hover:bg-muted"
                      onClick={() => handleReplyClick(comment)}
                      aria-label={`Reply to ${comment.author}'s comment`}
                    >
                      <Reply className="w-3 h-3 mr-1" />
                      Reply
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs h-7 px-2 hover:bg-muted"
                    aria-label="Like this comment"
                  >
                    <ThumbsUp className="w-3 h-3 mr-1" />
                    Like
                  </Button>
                </div>
              </div>
            </header>
          </CardContent>
        </Card>

        {/* Render replies with depth limiting */}
        {comment.replies && comment.replies.length > 0 && (
          <div
            className="mt-4 space-y-4"
            role="group"
            aria-label={`Replies to ${comment.author}'s comment`}
          >
            {comment.replies.slice(0, 10).map((reply) => renderComment(reply, true, depth + 1))}
            {comment.replies.length > 10 && (
              <p className="text-sm text-muted-foreground text-center py-2">
                And {comment.replies.length - 10} more replies...
              </p>
            )}
          </div>
        )}
      </article>
    )
  }, [formatDate, handleReplyClick])

  // Enhanced loading state with skeleton
  if (loading) {
    return (
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="space-y-8">
          {/* Back navigation skeleton */}
          <div className="h-6 bg-muted rounded animate-pulse w-32" />

          {/* Header skeleton */}
          <header className="space-y-6">
            <div className="flex flex-wrap items-center gap-2">
              <div className="h-6 bg-muted rounded animate-pulse w-20" />
              <div className="h-6 bg-muted rounded animate-pulse w-24" />
            </div>
            <div className="space-y-4">
              <div className="h-12 bg-muted rounded animate-pulse w-3/4" />
              <div className="h-6 bg-muted rounded animate-pulse w-full" />
              <div className="h-6 bg-muted rounded animate-pulse w-2/3" />
            </div>
            <div className="flex gap-2">
              <div className="h-6 bg-muted rounded animate-pulse w-16" />
              <div className="h-6 bg-muted rounded animate-pulse w-16" />
            </div>
          </header>

          {/* Content skeleton */}
          <div className="space-y-4">
            <div className="h-4 bg-muted rounded animate-pulse w-full" />
            <div className="h-4 bg-muted rounded animate-pulse w-full" />
            <div className="h-4 bg-muted rounded animate-pulse w-5/6" />
            <div className="h-4 bg-muted rounded animate-pulse w-full" />
          </div>

          <div className="flex justify-center items-center min-h-[200px]">
            <Loader />
          </div>
        </div>
      </main>
    )
  }

  // Enhanced error state with better UX
  if (error || !blogPost) {
    const isNotFound = error?.includes("not found") || error?.includes("404")

    return (
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="text-center py-16 space-y-6">
          <div className="space-y-4">
            <div className="w-16 h-16 mx-auto bg-destructive/10 rounded-full flex items-center justify-center">
              <MessageCircle className="w-8 h-8 text-destructive" />
            </div>
            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-foreground">
                {isNotFound ? "Blog Post Not Found" : "Something Went Wrong"}
              </h1>
              <p className="text-muted-foreground max-w-md mx-auto">
                {isNotFound
                  ? "The blog post you're looking for doesn't exist or may have been moved."
                  : error || "We encountered an error while loading this blog post."
                }
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => navigate("/blogs")} className="min-w-[140px]">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Button>
            <Button
              variant="outline"
              onClick={() => window.location.reload()}
              className="min-w-[140px]"
            >
              Try Again
            </Button>
          </div>

          {isNotFound && (
            <p className="text-sm text-muted-foreground">
              Redirecting to blog list in a few seconds...
            </p>
          )}
        </div>
      </main>
    )
  }

  return (
    <>
      <Seo
        type="article"
        title={blogPost?.metaTitle || blogPost?.title ? String(blogPost.metaTitle || blogPost.title) : undefined}
        description={blogPost?.metaDescription || blogPost?.excerpt ? String(blogPost.metaDescription || blogPost.excerpt) : undefined}
        image={absoluteImageUrl}
        url={blogPost?.slug ? getCanonicalUrl(`/blog/${blogPost.slug}`) : undefined}
        canonical={blogPost?.slug ? getCanonicalUrl(`/blog/${blogPost.slug}`) : undefined}
        keywords={seoData.seoTags.length > 0 ? seoData.seoTags.join(', ') : (blogPost?.category ? String(blogPost.category) : undefined)}
        publishedTime={seoData.publishDate}
        modifiedTime={seoData.publishDate}
        article={seoData.articleData}
      />

      <main className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 md:py-12 max-w-[720px]">
          {/* Blog Post Article - WordPress Style */}
          <article
            className="space-y-6"
            itemScope
            itemType="https://schema.org/BlogPosting"
            aria-labelledby="article-title"
          >
            {/* Featured Image - WordPress places it first */}
            {/* {blogPost.featuredImage && blogPost.featuredImage !== "/placeholder.svg" && (
              <figure className="w-full mb-8">
                <img
                  src={blogPost.featuredImage}
                  alt={`Featured image for: ${blogPost?.title || 'Blog Post'}`}
                  className="w-full h-auto object-cover"
                  loading="eager"
                  decoding="async"
                  itemProp="image"
                  onError={(e) => {
                    console.warn('Featured image failed to load:', blogPost.featuredImage)
                    e.target.src = "/placeholder.svg"
                    e.target.alt = "Image not available"
                  }}
                />
              </figure>
            )} */}

            {/* Article Header */}
            <header className="space-y-4 mb-8">
              {/* Article title - Well-displayed with proper spacing, typography, and word wrapping */}
              <h1
                id="article-title"
                className="text-3xl md:text-4xl lg:text-4xl font-bold text-foreground leading-tight tracking-tight mb-4"
                style={{
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  hyphens: 'auto',
                  textAlign: 'left',
                  maxWidth: '100%',
                }}
                itemProp="headline"
              >
                {blogPost?.title || 'Blog Post'}
              </h1>

              {/* Meta information - WordPress style: simple, below title */}
              <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                <time
                  dateTime={blogPost.publishDate}
                  itemProp="datePublished"
                  className="hover:text-foreground transition-colors"
                >
                  {formatDate(blogPost.publishDate)}
                </time>
                {blogPost.author && (
                  <>
                    <span aria-hidden="true">by</span>
                    <span itemProp="author" itemScope itemType="https://schema.org/Person">
                      <span itemProp="name" className="hover:text-foreground transition-colors">
                        {blogPost.author}
                      </span>
                    </span>
                  </>
                )}
                {blogPost.category && (
                  <>
                    <span aria-hidden="true">in</span>
                    <Link
                      to={`/blogs?category=${blogPost.category}`}
                      className="hover:text-foreground hover:underline transition-colors"
                    >
                      {blogPost.category}
                    </Link>
                  </>
                )}
              </div>
            </header>

            {/* Article Content - Preserve editor formatting */}
            {blogPost.content && (
              <>
                <div 
                  className="blog-content"
                  itemProp="articleBody"
                  dangerouslySetInnerHTML={{ __html: blogPost.content }}
                />
                <style>{`
                  .blog-content {
                    font-size: 16px;
                    line-height: 1.75;
                    color: hsl(var(--foreground));
                    font-feature-settings: "kern" 1, "liga" 1;
                    text-rendering: optimizeLegibility;
                    -webkit-font-smoothing: antialiased;
                  }
                  .blog-content p {
                    margin: 1em 0 !important;
                    color: hsl(var(--foreground));
                    line-height: 1.75;
                  }
                  .blog-content h1 {
                    font-size: 2em;
                    font-weight: bold;
                    margin: 1em 0 0.5em 0;
                    color: hsl(var(--foreground));
                  }
                  .blog-content h2 {
                    font-size: 1.5em;
                    font-weight: bold;
                    margin: 1em 0 0.5em 0;
                    color: hsl(var(--foreground));
                  }
                  .blog-content h3 {
                    font-size: 1.25em;
                    font-weight: bold;
                    margin: 1em 0 0.5em 0;
                    color: hsl(var(--foreground));
                  }
                  .blog-content h4,
                  .blog-content h5,
                  .blog-content h6 {
                    font-weight: bold;
                    margin: 1em 0 0.5em 0;
                    color: hsl(var(--foreground));
                  }
                  .blog-content a {
                    color: hsl(var(--primary));
                    text-decoration: underline;
                  }
                  .blog-content a:hover {
                    color: hsl(var(--primary) / 0.8);
                  }
                  .blog-content img {
                    max-width: 100%;
                    height: auto;
                    margin: 1em 0;
                    border-radius: 4px;
                  }
                  .blog-content div[data-youtube-video] {
                    position: relative;
                    padding-bottom: 56.25%;
                    height: 0;
                    overflow: hidden;
                    max-width: 100%;
                    margin: 1em 0;
                  }
                  .blog-content div[data-youtube-video] iframe {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    border: 0;
                  }
                  .blog-content strong,
                  .blog-content b {
                    font-weight: 600;
                  }
                  .blog-content em,
                  .blog-content i {
                    font-style: italic;
                  }
                  .blog-content u {
                    text-decoration: underline;
                  }
                  .blog-content strike,
                  .blog-content s {
                    text-decoration: line-through;
                  }
                  .blog-content blockquote {
                    border-left: 4px solid #ccc;
                    padding-left: 1em;
                    margin: 1em 0;
                    font-style: italic;
                  }
                  .blog-content code {
                    background-color: hsl(var(--muted));
                    padding: 0.125rem 0.375rem;
                    border-radius: 0.25rem;
                    font-size: 0.875em;
                    font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace;
                  }
                  .blog-content pre {
                    background-color: hsl(220 13% 18%);
                    color: hsl(var(--muted-foreground));
                    padding: 1em;
                    border-radius: 0.5rem;
                    overflow-x: auto;
                    margin: 1em 0;
                  }
                  .blog-content pre code {
                    background: none;
                    padding: 0;
                  }
                  .blog-content img {
                    max-width: 100%;
                    height: auto;
                    margin: 1em 0;
                  }
                  .blog-content ul,
                  .blog-content ol {
                    padding-left: 2em;
                    margin: 1em 0;
                  }
                  .blog-content li {
                    margin: 0.5em 0;
                  }
                  .blog-content hr {
                    border: none;
                    border-top: 1px solid hsl(var(--border));
                    margin: 2em 0;
                  }
                  .blog-content table {
                    width: 100%;
                    border-collapse: collapse;
                    margin: 1em 0;
                  }
                  .blog-content th {
                    background-color: hsl(var(--muted));
                    padding: 0.75rem;
                    text-align: left;
                    font-weight: 600;
                    border: 1px solid hsl(var(--border));
                  }
                  .blog-content td {
                    padding: 0.75rem;
                    border: 1px solid hsl(var(--border));
                  }
                `}</style>
              </>
            )}

            {/* Tags - WordPress style: at bottom of content */}
            {blogPost.tags && blogPost.tags.length > 0 && (
              <div className="pt-6 border-t border-border">
                <div className="flex flex-wrap items-center gap-2" aria-label="Article tags">
                  <span className="text-sm text-muted-foreground">Tags:</span>
                  {blogPost.tags.map((tag, index) => (
                    <Link
                      key={`${tag}-${index}`}
                      to={`/blogs?tag=${tag}`}
                      className="text-sm text-primary hover:underline"
                    >
                      {tag}
                      {index < blogPost.tags.length - 1 && ','}
                    </Link>
                  ))}
                </div>
              </div>
            )}

          </article>

          {/* Comments Section */}
          <section
            id="comments-section"
            className="mt-16 pt-12 border-t border-border space-y-8"
            aria-labelledby="comments-heading"
          >
            <header className="flex items-center gap-3 pb-4">
              <MessageCircle className="w-5 h-5 text-primary" aria-hidden="true" />
              <h2
                id="comments-heading"
                className="text-2xl font-bold text-foreground"
              >
                Comments ({getTotalCommentsCount(comments)})
              </h2>
            </header>

            {/* Comment Form with improved UX */}
            <Card
              ref={commentFormRef}
              id="comment-form"
              className="shadow-sm border-muted"
              role="region"
              aria-labelledby="comment-form-heading"
            >
              <CardHeader className="pb-4">
                <CardTitle
                  id="comment-form-heading"
                  className="text-lg flex items-center gap-2"
                >
                  {commentForm.replyingTo ? (
                    <>
                      <Reply className="w-5 h-5" />
                      Reply to {commentForm.replyingTo.author}
                    </>
                  ) : (
                    <>
                      <MessageCircle className="w-5 h-5" />
                      Leave a Comment
                    </>
                  )}
                </CardTitle>
                {commentForm.replyingTo && (
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg border max-w-md">
                      <MessageCircle className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-muted-foreground mb-1">
                          {commentForm.replyingTo.author} said:
                        </p>
                        <p className="text-sm text-gray-700 line-clamp-2">
                          {commentForm.replyingTo.content.replace(/<[^>]*>/g, '')}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => commentForm.setReplyingTo(null)}
                      className="text-muted-foreground hover:text-foreground"
                      aria-label="Cancel reply"
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCommentSubmit} className="space-y-6" noValidate>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="comment-name" className="text-sm font-medium">
                        Name <span className="text-destructive" aria-label="required">*</span>
                      </Label>
                      <Input
                        id="comment-name"
                        type="text"
                        value={commentForm.newComment.name}
                        onChange={(e) => commentForm.updateComment("name", e.target.value)}
                        placeholder="Your full name"
                        className="transition-colors focus:ring-2"
                        required
                        aria-describedby="name-error"
                        maxLength={50}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="comment-email" className="text-sm font-medium">
                        Email <span className="text-destructive" aria-label="required">*</span>
                      </Label>
                      <Input
                        id="comment-email"
                        type="email"
                        value={commentForm.newComment.email}
                        onChange={(e) => commentForm.updateComment("email", e.target.value)}
                        placeholder="your.email@example.com"
                        className="transition-colors focus:ring-2"
                        required
                        aria-describedby="email-error"
                        maxLength={100}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="comment-content" className="text-sm font-medium">
                      Comment <span className="text-destructive" aria-label="required">*</span>
                    </Label>
                    <Textarea
                      id="comment-content"
                      value={commentForm.newComment.comment}
                      onChange={(e) => commentForm.updateComment("comment", e.target.value)}
                      placeholder={
                        commentForm.replyingTo
                          ? `Reply to ${commentForm.replyingTo.author}...`
                          : "Share your thoughts, ask questions, or start a discussion..."
                      }
                      className="min-h-[120px] resize-y transition-colors focus:ring-2"
                      required
                      aria-describedby="comment-error"
                      maxLength={2000}
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span id="comment-error" role="status" aria-live="polite"></span>
                      <span>
                        {commentForm.newComment.comment.length}/2000 characters
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="sm:w-auto w-full min-w-[140px]"
                      aria-describedby={isSubmitting ? "submitting-status" : undefined}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Posting...
                        </>
                      ) : commentForm.replyingTo ? (
                        <>
                          <Reply className="w-4 h-4 mr-2" />
                          Post Reply
                        </>
                      ) : (
                        <>
                          <MessageCircle className="w-4 h-4 mr-2" />
                          Post Comment
                        </>
                      )}
                    </Button>

                    {commentForm.replyingTo && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => commentForm.setReplyingTo(null)}
                        disabled={isSubmitting}
                        className="sm:w-auto w-full"
                      >
                        Cancel Reply
                      </Button>
                    )}
                  </div>

                  {/* Screen reader status */}
                  <div id="submitting-status" className="sr-only" aria-live="polite">
                    {isSubmitting ? "Submitting your comment..." : ""}
                  </div>

                  {/* Privacy notice */}
                  <p className="text-xs text-muted-foreground border-t pt-4">
                    Your email address will not be published. Required fields are marked with *
                  </p>
                </form>
              </CardContent>
            </Card>

            {/* Comments List with enhanced structure */}
            {comments.length > 0 ? (
              <div
                className="space-y-6"
                role="region"
                aria-label={`${comments.length} comments`}
              >
                {comments.map((comment) => renderComment(comment))}
              </div>
            ) : (
              <Card className="border-dashed">
                <CardContent className="pt-8 pb-8 text-center">
                  <div className="space-y-4">
                    <div className="w-12 h-12 mx-auto bg-muted rounded-full flex items-center justify-center">
                      <MessageCircle className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-lg font-medium text-foreground">No comments yet</h3>
                      <p className="text-muted-foreground max-w-sm mx-auto">
                        Be the first to share your thoughts and start a conversation!
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </section>
        </div>
      </main>
    </>
  )
}
